/**
 * Grid (Table) Component
 *
 * Renders the resolver payload from `chart_resolver/grid.py` as a paginated
 * shadcn `Table`. The backend returns:
 *   - `columns: [{ key, label, type, dsid, field_id }]`
 *   - `rows: Record<string, unknown>[]`
 *   - `total_count` and `page_size`
 *
 * Pagination is currently client-side over the rows that the resolver
 * returns (the resolver itself enforces a server-side cap via `paging_count`).
 */

import { useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableFooter,
} from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIndicatorData, IndicatorGridColumn } from './useIndicatorData';
import { useDrilldown } from './useDrilldown';
import { usePagination } from '../../../hooks/usePagination';
import { matchFormatRule } from '../builder/ConditionalFormatPanel';
import { type BuilderFormatRule } from '../../../lib/api/reporting';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface GridComponentProps {
  indicatorId?: number;
  /** Override the scroll-container max height (default 320 px) — the full-view
   *  dialog passes a larger value so the full dataset is visible at once. */
  maxHeight?: number | string;
}

function DrilldownCell({
  column,
  value,
  rules,
}: {
  column: IndicatorGridColumn;
  value: unknown;
  rules?: BuilderFormatRule[];
}) {
  const onClick = useDrilldown(column.drilldown_url);
  const { formatDate } = useFormatters();
  const clickable = Boolean(column.drilldown_url);
  const rule = matchFormatRule(rules, value, column.label);
  const style: React.CSSProperties = {};
  if (rule?.color) style.color = rule.color;
  if (rule?.background) style.backgroundColor = rule.background;
  return (
    <TableCell
      className={`text-xs py-2 ${clickable ? 'cursor-pointer hover:underline' : ''}`}
      style={style}
      onClick={clickable ? () => onClick(value) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {formatCell(value, column.type, formatDate)}
    </TableCell>
  );
}


function formatCell(
  value: unknown,
  type: string,
  formatDate: (d: string | Date) => string,
): string {
  if (value === null || value === undefined) return '—';
  if (type === 'date' || type === 'datetime') {
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return formatDate(value);
    }
  }
  if (type === 'decimal' || type === 'int') {
    const n = Number(value);
    if (Number.isFinite(n)) return n.toLocaleString();
  }
  if (type === 'bool') return value ? 'Yes' : 'No';
  return String(value);
}

export function GridComponent({ indicatorId, maxHeight = 320 }: GridComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);

  const columns: IndicatorGridColumn[] = useMemo(() => live?.columns || [], [live]);
  const rows: Record<string, unknown>[] = useMemo(() => live?.rows || [], [live]);
  const totalCount = live?.total_count ?? rows.length;
  const formatRules = (live?.format_rules as BuilderFormatRule[] | undefined) ?? [];

  const pagination = usePagination({ pageSize: 10, scrollToTop: false });

  const pagedRows = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return rows.slice(start, end);
  }, [rows, pagination.currentPage, pagination.pageSize]);

  // Sync local page count whenever the row count changes
  const totalPages = Math.max(1, Math.ceil(rows.length / pagination.pageSize));

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
        No columns configured.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className="overflow-auto rounded-md border"
        style={{ maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }}
      >
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className="text-xs whitespace-nowrap">{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground text-sm">
                  No rows.
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((c) => (
                    <DrilldownCell key={c.key} column={c} value={row[c.key]} rules={formatRules} />
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={columns.length} className="text-xs">
                Total: {totalCount.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={pagination.currentPage <= 1}
            onClick={() => pagination.setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>Page {pagination.currentPage} of {totalPages}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={pagination.currentPage >= totalPages}
            onClick={() => pagination.setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
